
import { defineComponent, ref, reactive } from 'vue'
import { SummaryData } from '../../types'
import KpiSummary from './KpiSummary.vue'

export default defineComponent({
    name: 'Summary',
    components: {
        KpiSummary,
    },
    props: {
        summaryData: {
            type: Object as () => SummaryData | null,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        shifts: {
            type: Array,
            default: () => [],
        },
        plans: {
            type: Array,
            default: () => [],
        },
    },
    setup(props) {
        const isExpanded = ref(true)
        const sectionsExpanded = reactive({
            kpi: true,
        })
        const overallKpiValue = ref<number | null>(null)
        const averageKpiValue = ref<number | null>(null)

        const toggleExpanded = () => {
            isExpanded.value = !isExpanded.value
        }

        const toggleSection = (section: keyof typeof sectionsExpanded) => {
            sectionsExpanded[section] = !sectionsExpanded[section]
        }

        const onUpdateOverallKpi = (value: { kpi: number; average: number } | number) => {
            if (typeof value === 'object') {
                overallKpiValue.value = value.kpi
                averageKpiValue.value = value.average
            } else {
                overallKpiValue.value = value
                averageKpiValue.value = null
            }
        }

        const formatCurrency = (amount: number): string => {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                minimumFractionDigits: 0,
            }).format(amount)
        }

        const getKpiValueClass = (percentage: number): string => {
            if (percentage >= 100) return 'kpi-success'
            if (percentage >= 80) return 'kpi-warning'
            return 'kpi-danger'
        }

        const getRevenueValueClass = (percentage: number): string => {
            if (percentage >= 100) return 'revenue-success'
            if (percentage >= 80) return 'revenue-warning'
            return 'revenue-danger'
        }

        const getTotalShifts = (): number => {
            return props.shifts.length
        }

        const getLowPerformanceShifts = (): number => {
            if (!props.shifts.length || !props.plans.length) return 0

            let lowPerformanceCount = 0
            ;(props.shifts as any[]).forEach((shift: any) => {
                const plan = (props.plans as any[]).find((p: any) => p.id === shift.planId)
                if (plan && plan.dailyRevenuePlan) {
                    const targetRevenue =
                        plan.dailyRevenuePlan.general || plan.dailyRevenuePlan[shift.branchId] || 0
                    const actualRevenue = shift.dailyRevenue || 0
                    const performancePercentage =
                        targetRevenue > 0 ? (actualRevenue / targetRevenue) * 100 : 0

                    if (performancePercentage < 80) {
                        lowPerformanceCount++
                    }
                }
            })
            return lowPerformanceCount
        }

        const getTotalPlannedRevenue = (): number => {
            if (!props.shifts.length || !props.plans.length) return 0

            let totalPlanned = 0
            ;(props.shifts as any[]).forEach((shift: any) => {
                const plan = (props.plans as any[]).find((p: any) => p.id === shift.planId)
                if (plan && plan.dailyRevenuePlan) {
                    const plannedRevenue =
                        plan.dailyRevenuePlan.general || plan.dailyRevenuePlan[shift.branchId] || 0
                    totalPlanned += plannedRevenue
                }
            })
            return totalPlanned
        }

        const getTotalActualRevenue = (): number => {
            if (!props.shifts.length) return 0

            let totalActual = 0
            ;(props.shifts as any[]).forEach((shift: any) => {
                totalActual += parseFloat(shift.dailyRevenue) || 0
            })
            return totalActual
        }

        const getRevenuePerformancePercentage = (): number => {
            const planned = getTotalPlannedRevenue()
            const actual = getTotalActualRevenue()
            return planned > 0 ? Math.round((actual / planned) * 100) : 0
        }

        return {
            isExpanded,
            sectionsExpanded,
            overallKpiValue,
            averageKpiValue,
            toggleExpanded,
            toggleSection,
            onUpdateOverallKpi,
            formatCurrency,
            getKpiValueClass,
            getRevenueValueClass,
            getTotalShifts,
            getLowPerformanceShifts,
            getTotalPlannedRevenue,
            getTotalActualRevenue,
            getRevenuePerformancePercentage,
        }
    },
})
